import { RootState } from "../../app/store";
import { createSelector } from "reselect";
import {
  FlightStatus,
  isCriticalTelemetry,
  isVerboseTelemetry,
} from "./types";
import { isBoolean } from "lodash";
import { getDistanceBetweenPoints } from "../../geometry";
import { cellData } from "./state";
import { selectFcu } from "../../tools/parse"; 

export const staticIp = "nano.m4mga3.com";

export const selectClientId = (state: RootState) => state.aircraft.clientId;
export const selectLive = (state: RootState) => state.aircraft.live;
export const selectLastEvent = (state: RootState) => state.aircraft.lastEvent;
export const selectDirect = (state: RootState) => state.aircraft.direct;
export const selectUavList = (state: RootState) => state.aircraft.uavList;
export const selectConnections = (state: RootState) =>
  state.aircraft.connections;
export const selectNotifications = (state: RootState) =>
  state.aircraft.notifications;
export const selectLandingPads = (state: RootState) =>
  state.aircraft.landingPads;
export const selectCommand = (state: RootState) => state.aircraft.command;
export const selectAircraftId = (_: RootState, uavId: string) => uavId;
export const selectAircraftConnectionById = createSelector(
  selectConnections,
  selectAircraftId,
  (connections, id) => {
    return connections[id];
  }
);
export const selectAircraftUrlById = createSelector(
  selectAircraftConnectionById,
  (connection) => {
    return connection.sims.find((sim) => sim.active)?.ip;
  }
);

export const selectAircraftById = createSelector(
  selectLive,
  selectAircraftId,
  (live, id) => {
    return live[id];
  }
);

export const selectDirectById = createSelector(
  selectDirect,
  selectAircraftId,
  (direct, id) => {
    return direct[id];
  }
);
export const selectFlightModeById = createSelector(
  selectDirect,
  selectAircraftId,
  (direct, id) => {
    const payload = direct[id]?.payload;
    if (isCriticalTelemetry(payload)) {
      return payload?.flight_mode;
    }

    return selectFcu(payload)?.flight_mode;
  }
);
export const selectMissionId = createSelector(
  selectDirect,
  selectAircraftId,
  (direct, id) => {
    const payload = direct[id]?.payload;
    if (!payload) return null;
    if (isCriticalTelemetry(payload)) {
      return payload?.mission_id;
    }

    return payload.mission_id;
  }
);

export const selectFlightPhaseById = createSelector(
  selectDirect,
  selectAircraftId,
  selectLastEvent,
  (direct, id, events): FlightStatus | null => {
    const payload = direct[id]?.payload;
    if (!payload) return null;

    const currentSubState = payload.uav_sub_state;
    const event = events?.[id];
    const inFlight = currentSubState?.includes("STATE_FLIGHT");

    if (inFlight) {
      if (currentSubState?.includes("STATE_FLIGHT_EOPS")) {
        return "abort";
      }

      if (event === "MISSION_STOPPED") {
        return "missionComplete";
      }

      if (currentSubState?.includes("STATE_FLIGHT_MISSION_OUTBOUND_TAKEOFF")) {
        return "takeoff";
      }

      if (event === "DELIVERY_COMMAND_ENDED" && currentSubState?.includes("STATE_FLIGHT_MISSION_OUTBOUND_DESCENDING")) {
        return "atDropLocation";
      }

      if (
        currentSubState?.includes(
          "STATE_FLIGHT_MISSION_OUTBOUND_WAITING_FOR_DELIVERY_CONFIRMATION"
        ) ||
        currentSubState?.includes("STATE_FLIGHT_MISSION_OUTBOUND_DESCENDING")
      ) {
        return "pendingDelivery";
      }

      if (currentSubState?.includes("STATE_FLIGHT_MISSION_DELIVERY_OPENING_DOORS")) {
        return "openingDoors";
      }

      if (currentSubState?.includes("STATE_FLIGHT_MISSION_DELIVERY_WAITING_FOR_PAYLOAD_TOUCHDOWN")
        || currentSubState?.includes("STATE_FLIGHT_MISSION_DELIVERY_ASCENDING_TO_CUT")){
        return "dropping";
      }

      if (currentSubState?.includes("STATE_FLIGHT_MISSION_INBOUND_ASCENDING_TO_CRUISE")) {
        return "climbDelivery";
      }
      if (currentSubState?.includes("STATE_FLIGHT_MISSION_INBOUND") ||
          currentSubState?.includes("STATE_FLIGHT_MISSION_DELIVERY_CLOSING_DOORS") ||
          currentSubState?.includes("STATE_FLIGHT_MISSION_INBOUND_CRUISE_INBOUND")) {
        return "returning";
      }

      if (payload.uav_state === "MAINTENANCE") return "maintenance";
      if (payload.uav_state === "ABORT") return "abort";

      if (!payload.mission_id || payload.uav_state === "STANDBY") {
        return "standby";
      }

      if (event === "MISSION_STARTED") {
        return "takeoff";
      } else if (event === "MISSION_IN_FLIGHT") {
        return "flight";
      } else if (event === "DELIVERY_STARTED") {
        return "dropping";
      } else if (event === "DOORS_OPEN" || event === "DELIVERY_COMMAND_ENDED") {
        return "openingDoors";
      } else if (event === "DOORS_CLOSED") {
        return "returning";
      } else if (event === "MISSION_TERMINATED") {
        return "abort";
      }

      if (currentSubState?.includes("STATE_FLIGHT")) {
        return "flight";
      }

      return "ready";
    }

    if (event === "MISSION_STOPPED") {
      return "missionComplete";
    }

    if (
      payload.mission_id &&
      (payload.uav_state === "STANDBY" || 
       payload.uav_state === "MOTORSTEST" || 
       (!inFlight && payload.uav_state === "MISSION"))
    ) {
      return "missionReceived";
    }

    if (payload.uav_state === "MAINTENANCE") return "maintenance";
    if (payload.uav_state === "ABORT") return "abort";
    if (!payload.mission_id || payload.uav_state === "STANDBY") {
      return "standby";
    }

    return "ready";
  }
);

export const selectNotificationsById = createSelector(
  selectNotifications,
  selectAircraftId,
  (notifications, id) => {
    return notifications[id];
  }
);

export const selectLandingPadById = createSelector(
  selectLandingPads,
  selectAircraftId,
  (landingPads, id) => {
    return landingPads[id];
  }
);

export const selectAvailableCraft = createSelector(
  [selectLive, (state: RootState) => state.session.cell],
  (live, cell) => {
    if (live) {
      return Object.keys(live)?.filter((uavId) => {
        const cellCentre =
          cell?.centerLatitude && cell?.centerLongitude
            ? [cell?.centerLatitude, cell?.centerLongitude]
            : null;
        const uav = live[uavId];
        const latLon: any =
          cellCentre || cellData?.[cell?.cellName || ""]?.latLon;
        if (!latLon) return false;
        const dist = getDistanceBetweenPoints(uav.currentPosition, latLon);

        if (dist < 20000) return true;
        return false;
      });
    }
    return [];
  }
);

export const selectUavById = createSelector(
  selectUavList,
  selectAircraftId,
  (uavList, uavId) => {
    return uavList.find((uav) => {
      return uav?.platformId?.includes(uavId);
    });
  }
);

export const selectAircraftVitalsById = createSelector(
  selectDirect,
  selectAircraftId,
  (direct, id) => {
    const payload = direct[id]?.payload;
    if (isCriticalTelemetry(payload)) {
      return {
        flightMode: payload?.flight_mode,
        heading: payload?.heading,
        altitude: payload?.alt_lidar,
        relativeAlt: payload?.alt,
        lat: payload?.lat,
        lon: payload?.lon,
        uavState: payload?.uav_state,
        uavSubState: payload?.uav_sub_state,
        missionId: payload?.mission_id,
        battery: payload?.battery_voltage,
        fcuInControl: payload?.fcu_in_control,
        fcu: payload?.fcu,
        armed: true,
      };
    }
    const fcu = selectFcu(payload);

    return {
      flightMode: fcu?.flight_mode,
      heading: fcu?.compass?.heading,
      altitude: fcu?.alt?.lidar,
      relativeAlt: fcu?.alt?.relitive_alt,
      lat: fcu?.gps?.lat,
      lon: fcu?.gps?.lon,
      armed: fcu?.armed,
      uavState: payload?.uav_state,
      uavSubState: payload?.uav_sub_state,
      departureTime: payload?.departure_time,
      missionId: payload?.mission_id,
      autoArmStatus: payload?.auto_arm_status,
      battery: payload?.battery_volts,
      fcuInControl: payload?.fcu_in_control,
      fcu,
    };
  }
);

const selectUavMissions = (state: RootState) => state.mission.uavMissions;
export const selectAircraftVitals = createSelector(
  selectDirect,
  selectLive,
  selectUavMissions,
  (direct, live, missions) => {
    const directData = Object.values(direct).map((uav) => {
      const payload = uav?.payload;

      if (isCriticalTelemetry(payload)) {
        return {
          controlled: true,
          uavId: payload.uav_id,
          flightMode: payload?.flight_mode,
          heading: payload?.heading,
          altitude: payload?.alt_lidar,
          relativeAlt: payload?.alt,
          lat: payload?.lat,
          lon: payload?.lon,
          uavState: payload?.uav_state,
          uavSubState: payload?.uav_sub_state,
          missionId: payload?.mission_id,
          mission: missions[payload?.uav_id],
        };
      }
      const fcu = selectFcu(payload);
      const mission = payload.uav_id ? missions?.[payload.uav_id] : null;

      return {
        controlled: true,
        uavId: payload.uav_id,
        flightMode: fcu?.flight_mode,
        heading: fcu?.compass?.heading,
        altitude: fcu?.alt?.lidar,
        relativeAlt: fcu?.alt?.relitive_alt,
        lat: fcu?.gps?.lat,
        lon: fcu?.gps?.lon,
        uavState: payload?.uav_state,
        uavSubState: payload?.uav_sub_state,
        departureTime: payload?.departure_time,
        missionId: payload?.mission_id,
        mission,
      };
    });

    const directUavs = Object.keys(direct);

    const liveData = Object.values(live)
      .map((uav) => {
        if (directUavs.includes(uav.uavId)) return null;
        return {
          controlled: false,
          uavId: uav.uavId,
          flightMode: uav.flightMode,
          heading: uav.compass,
          altitude: uav.alt?.lidar,
          relativeAlt: null,
          lat: uav.pos?.lat,
          lon: uav.pos?.lon,
          uavState: uav.uavState,
          uavSubState: uav?.uavSubState,
          missionId: uav.mission?.missionId,
          mission: missions[uav?.uavId],
        };
      })
      .filter(Boolean);

    return [...directData, ...liveData];
  }
);

export const selectSelectedUavId = (state: RootState) =>
  state.aircraft.selectedUav;
export const selectHoverUav = (state: RootState) => state.aircraft.hoverUav;

export const selectSelectedUav = createSelector(
  [selectSelectedUavId, selectLive],
  (selectedUav, live) => {
    return Object.values(live).find((uav) => {
      return uav.uavId === selectedUav;
    });
  }
);
export const selectSelectedUavDirect = createSelector(
  [selectSelectedUavId, selectDirect],
  (selectedUav, direct) => {
    return Object.values(direct).find((uav) => {
      return uav.payload.uav_id === selectedUav;
    });
  }
);

export const selectUavMissionId = createSelector(
  [selectAircraftId, selectDirect],
  (selectedUav, direct) => {
    return Object.values(direct).find((uav) => {
      return uav.payload.uav_id === selectedUav;
    })?.payload?.mission_id;
  }
);
export const selectUavDirectById = createSelector(
  [selectAircraftId, selectDirect],
  (uavId, direct) => {
    return Object.values(direct).find((uav) => {
      return uav.payload.uav_id === uavId;
    });
  }
);

export const selectMap = createSelector(
  [selectAircraftId, selectDirect],
  (uavId, direct) => {
    return Object.values(direct).find((uav) => {
      return uav.payload.uav_id === uavId;
    });
  }
);

export const selectOtherAircraft = createSelector(
  [selectLive, selectConnections, selectDirect],
  (live, connections, direct) => {
    return Object.values(live).filter((uav) => {
      if (!connections?.[uav.uavId]) {
        return true;
      }
      if (connections?.[uav.uavId] && !direct?.[uav.uavId]) {
        return true;
      }
      return false;
    });
  }
);
export const selectAvailableCraftAuto = createSelector(
  [selectLive, (state: RootState) => state.session.cell, selectDirect],
  (live, cell, direct) => {
    if (live) {
      return Object.keys(live)?.filter((uavId) => {
        const uav = live[uavId];
        const directTelem = direct[uavId];

        const latLon = cellData?.[cell?.cellName || ""]?.latLon;
        if (!latLon) return false;
        const dist = getDistanceBetweenPoints(
          uav.currentPosition,
          cellData[cell?.cellName || ""]?.latLon
        );
        if (
          dist < 3000 &&
          isVerboseTelemetry(directTelem?.payload) &&
          isBoolean(directTelem?.payload.auto_arm_status) &&
          directTelem?.payload.auto_arm_status
        ) {
          return true;
        }
        return false;
      });
    }
    return [];
  }
);

export const connectedToCraft = createSelector(selectDirect, (direct) => {
  return direct && Object.keys(direct).length > 0;
});

export const selectUavGeofenceIds = (state: RootState) => state.aircraft.uavGeofenceIds;

export const selectGeofenceIdByUav = createSelector(
  selectUavGeofenceIds,
  selectAircraftId,
  (geofenceIds, uavId) => geofenceIds[uavId]
);
